import { useContext, useEffect, useMemo, useReducer, useState } from "react";

import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import _ from "lodash";
import { useSnackbar } from "notistack";

import { claimPaymentAmount } from "../../Insurance/InsuranceService";
import { ReportsIcon } from "../../Icons/Icons";
import { sortAmount } from "./helpers";
import { ReportViewer } from "./ReportViewer";

export default function ClaimsReport() {
  const { globalState, globalDispatch } = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();

  // State Management
  const [currentYear, setCurrentYear] = useState(globalState?.crop_year || getCurrentReinsuranceYear());
  const [filter, dispatchFilter] = useReducer(propUpdateReducer, {
    commodity_name: [],
    practice_name: [],
  });

  const [insuranceClaimsReportData, setInsuranceClaimsReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Update global crop year
  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: { key: "crop_year", value: currentYear },
    });
  }, [currentYear]);

  // Handle API Errors
  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || err, { preventDuplicate: true, variant: "error" });
  };

  // Fetch Insurance Claims Data
  useEffect(() => {
    const loadInsurancePlanClaims = async () => {
      try {
        if (!globalState?.producer_token) return;

        setIsLoading(true);

        const producerFilter = {
          producer_token: globalState.producer_token,
          is_active: true,
          reinsurance_year: currentYear,
          // Apply filters if they have values
          ...(filter.commodity_name?.length === 1 ? { commodity_name: filter.commodity_name[0] } : null),
          ...(filter.practice_name?.length === 1 ? { practice_name: filter.practice_name[0] } : null),
        };

        let _insuranceClaims = await UsersApiHelper.users_search("InsurancePlanClaim", producerFilter, globalState);

        if (filter.commodity_name?.length > 1 || filter.practice_name?.length > 1) {
          const filteredClaims = _insuranceClaims.filter((claim) => {
            const matchesCommodity = filter.commodity_name.includes(claim.commodity_name);
            const matchesPractice = filter.practice_name.includes(claim.practice_name);

            return matchesCommodity && matchesPractice;
          });

          _insuranceClaims = filteredClaims;
        }
        setInsuranceClaimsReportData(_insuranceClaims?.length ? _insuranceClaims : []);
      } catch (err) {
        setInsuranceClaimsReportData([]);
        handleError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadInsurancePlanClaims();
  }, [currentYear, filter, globalState?.producer_token]);

  // Claims Report Data - directly use the API response
  const claimsReportData = useMemo(() => {
    return insuranceClaimsReportData.map((claim) => ({
      ...claim,
      payment_amount: claimPaymentAmount(claim),
      claim_status: claim.stage_name || "N/A",
      damage_causes:
        [claim.first_damage_cause_name, claim.second_damage_cause_name, claim.third_damage_cause_name]
          .filter(Boolean)
          .join(", ") || "N/A",
      total_damage_percent:
        [claim.first_damage_percent, claim.second_damage_percent, claim.third_damage_percent]
          .filter(Boolean)
          .reduce((sum, percent) => sum + percent, 0) || 0,
    }));
  }, [insuranceClaimsReportData]);

  // Column Configuration - updated to match InsurancePlanClaim response
  const columnsDirective = [
    { field: "location_state_name", headerText: "State", width: 100 },
    { field: "location_county_name", headerText: "County", width: 100 },
    { field: "policy_number", headerText: "Policy #", width: 120 },
    { field: "insurance_plan_name", headerText: "Plan", width: 150 },
    { field: "commodity_name", headerText: "Crop", width: 120 },
    { field: "practice_name", headerText: "Practice", width: 150 },
    { field: "type_name", headerText: "Type", width: 120 },
    { field: "claim_number", headerText: "Claim #", width: 120 },
    { field: "claim_status", headerText: "Status", width: 150 },
    { field: "damage_date", headerText: "Damage Date", width: 120, type: "date" },
    { field: "notice_date", headerText: "Notice Date", width: 120, type: "date" },
    { field: "damage_causes", headerText: "Damage Causes", width: 200 },
    { field: "total_damage_percent", headerText: "Damage %", width: 120, textAlign: "Right", format: "P2" },
    { field: "determined_acreage", headerText: "Determined Acres", width: 150, textAlign: "Right", format: "N2" },
    {
      field: "aip_indemnity_amount",
      headerText: "Indemnity Amount",
      width: 150,
      textAlign: "Right",
      format: "C2",
      sortComparer: sortAmount,
    },
    {
      field: "harvested_production_quantity",
      headerText: "Harvested Production",
      width: 180,
      textAlign: "Right",
      format: "N2",
    },
    {
      field: "production_to_count_quantity",
      headerText: "Production to Count",
      width: 180,
      textAlign: "Right",
      format: "N2",
    },
    { field: "total_production_amount", headerText: "Total Production", width: 150, textAlign: "Right", format: "N2" },
    {
      field: "payment_amount",
      headerText: "Payment Amount",
      width: 150,
      textAlign: "Right",
      format: "C2",
      sortComparer: sortAmount,
    },
    {
      field: "payment_date",
      headerText: "Payment Date",
      width: 150,
      textAlign: "Right",
      type: "date",
    },
    {
      field: "note",
      headerText: "Note",
      width: 150,
    },
    { field: "is_settlement", headerText: "Settlement", width: 120, type: "boolean" },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Insurance Claims"
        category={{
          title: "Reports",
          icon: <ReportsIcon />,
        }}
      />
      <ReportViewer
        columnsDirective={columnsDirective}
        counties={[]} // Empty since we're not using farm controller
        producerFarmFieldCrops={claimsReportData}
        isLoadingCounties={isLoading}
        reportType="claims"
        currentYear={currentYear}
        setCurrentYear={setCurrentYear}
        gridKey="claimsReport"
        filter={filter}
        dispatchFilter={dispatchFilter}
      />
      {!isLoading && !claimsReportData?.length && (
        <EmptyCard text="No claims data found" secondaryText="Please check your filters or add more farm data." />
      )}
    </>
  );
}
